import { useContext, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper";
import Loading from "../ui/Loading";
import getClassName from "../../utilities/getClassName";
import { getIcon, getRawIcon } from "../../utilities/icon";
import PoweredByContext from "../../store/PoweredByContext";

// TODO: Serve real alt text from Umbraco on gethpaircraft images instead of inferring exterior/interior here.
const getAircraftImageAlt = (aircraftName, index) => {
    const view = index === 0 ? "exterior" : "interior";
    return aircraftName ? `${aircraftName} ${view}` : `Aircraft ${view}`;
};

const AircraftBrowserResults = ({ onUpdate, description, results, changeCategory, initialSlide }) => {
    // State
    const [showImageSlider, setShowImageSlider] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [atStart, setAtStart] = useState(false);
    const [atEnd, setAtEnd] = useState(false);
    const [swiperInit, setSwiperInit] = useState(false);
    const [changed, setChanged] = useState(false);
    const [swipeDirection, setSwipeDirection] = useState(null);
    const [swiping, setSwiping] = useState(false);

    // Hooks
    const poweredByCtx = useContext(PoweredByContext);

    // Effect

    // When the results changes, if there are results,set the first one as the active one
    // otherwise unset the active state.
    // `changeCategory` and `initialSlide` are omitted: the parent
    // (AircraftBrowser) recreates `changeCategory` on every render. The
    // effect's real trigger is `results`.
    useEffect(() => {
        setAtStart(initialSlide === 0);
        setAtEnd(initialSlide !== 0);
        changeCategory(null);

        if (results) {
            setTimeout(() => {
                setShowResults(true);
            }, 100);
        } else {
            setShowResults(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results]);

    // Listen for window resize events
    useEffect(() => {
        const resizeHandler = () => {
            setShowImageSlider(!window.matchMedia("(min-width: 1024px)").matches);
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
    }, []);

    useEffect(() => {
        if (swiperInit) {
        }
    }, [swiperInit]);

    window.swipeTimeout = window.swipeTimeout || null;

    useEffect(() => {
        if (changed) {
            setChanged(false);
            setSwiping(false);
            setSwipeDirection(null);
            clearTimeout(window.swipeTimeout);
        }
    }, [changed]);

    // Triggered only by swiping; atStart/atEnd/swipeDirection are read from
    // the latest closure, and changeCategory is a parent callback we don't
    // want to add to deps (LuggageDropdown trap).
    useEffect(() => {
        if (swiping) {
            window.swipeTimeout = setTimeout(() => {
                if (swipeDirection === "prev" && atStart) {
                    changeCategory("previous");
                }
                if (swipeDirection === "next" && atEnd) {
                    changeCategory("next");
                }
                setSwiping(false);
                setSwipeDirection(null);
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swiping]);

    const navigationPrevRef = useRef();
    const navigationNextRef = useRef();

    const getResultsClassname = () => {
        let classNames = ["aircraft-browser-results"];

        if (showResults) {
            classNames.push("aircraft-browser-results--active");
        }

        return getClassName(classNames);
    };

    const handlePreviousClick = () => {
        if (!changed) {
            changeCategory("previous");
        } else {
            changeCategory(null);
        }
    };

    const handleNextClick = () => {
        if (!changed) {
            changeCategory("next");
        } else {
            changeCategory(null);
        }
    };

    return (
        <div className={getResultsClassname()}>
            <div className="aircraft-browser-results__text">
                <p>{description}</p>
            </div>
            {results && (
                <div className="aircraft-browser-results__swiper-container">
                    <div
                        className="aircraft-browser-results__swiper-nav aircraft-browser-results__swiper-nav--previous"
                        ref={navigationPrevRef}
                        onClick={handlePreviousClick}
                    >
                        {poweredByCtx.isPoweredBy ? (
                            <>{getRawIcon("chevron-left-gold")}</>
                        ) : (
                            <img src={getIcon("chevron-left-gold")} alt="" />
                        )}
                    </div>
                    <div
                        className="aircraft-browser-results__swiper-nav aircraft-browser-results__swiper-nav--next"
                        ref={navigationNextRef}
                        onClick={handleNextClick}
                    >
                        {poweredByCtx.isPoweredBy ? (
                            <>{getRawIcon("chevron-right-gold")}</>
                        ) : (
                            <img src={getIcon("chevron-right-gold")} alt="" />
                        )}
                    </div>
                    <Swiper
                        modules={[Navigation, Pagination, A11y]}
                        className="aircraft-browser-results__swiper"
                        slidesPerView={1}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        onBeforeInit={(swiper) => {
                            setSwiperInit(true);
                            setAtStart(swiper.activeIndex === 0);
                            setAtEnd(swiper.activeIndex + 1 === swiper.slides.length);
                            swiper.params.navigation.prevEl = navigationPrevRef.current;
                            swiper.params.navigation.nextEl = navigationNextRef.current;
                        }}
                        onSlideChange={(swiper) => {
                            setAtEnd(swiper.activeIndex + 1 === swiper.slides.length);
                            setChanged(true);
                            setSwiping(false);
                        }}
                        onSliderMove={(e) => {
                            if (e.swipeDirection) {
                                if (swipeDirection !== e.swipeDirection) {
                                    setSwipeDirection(e.swipeDirection);
                                    setSwiping(true);
                                }
                            }
                        }}
                        speed={600}
                        pagination={{
                            dot: true,
                        }}
                        initialSlide={initialSlide}
                    >
                        {results.map((result, resultID) => {
                            if (resultID < 3) {
                                return (
                                    <SwiperSlide key={resultID}>
                                        <article className="aircraft-browser-results__item">
                                            {!showImageSlider && (
                                                <div className="aircraft-browser-results__images">
                                                    {result.images.slice(0, 2).map((image, i) => (
                                                        <figure
                                                            className="aircraft-browser-results__image"
                                                            data-loading={true}
                                                            key={i}
                                                        >
                                                            <img
                                                                src={image}
                                                                onLoad={(e) => {
                                                                    setTimeout(() => {
                                                                        e.target.style.opacity = 1;
                                                                        delete e.target.parentNode.dataset.loading;
                                                                    }, 100);
                                                                }}
                                                                alt={getAircraftImageAlt(result.aircraftName, i)}
                                                            />
                                                        </figure>
                                                    ))}
                                                </div>
                                            )}
                                            {showImageSlider && (
                                                <Swiper
                                                    modules={[Navigation, Pagination, A11y]}
                                                    className="aircraft-browser-results__image-swiper"
                                                    slidesPerView={1}
                                                    navigation={true}
                                                    allowTouchMove={false}
                                                    speed={600}
                                                    pagination={{
                                                        type: "fraction",
                                                    }}
                                                >
                                                    {result.images.slice(0, 2).map((image, i) => (
                                                        <SwiperSlide key={i}>
                                                            <figure
                                                                className="aircraft-browser-results__image"
                                                                data-loading={true}
                                                            >
                                                                <img
                                                                    src={image}
                                                                    onLoad={(e) => {
                                                                        setTimeout(() => {
                                                                            e.target.style.opacity = 1;
                                                                            delete e.target.parentNode.dataset.loading;
                                                                        }, 100);
                                                                    }}
                                                                    alt={getAircraftImageAlt(result.aircraftName, i)}
                                                                />
                                                            </figure>
                                                        </SwiperSlide>
                                                    ))}
                                                </Swiper>
                                            )}
                                            <div className="aircraft-browser-results__content">
                                                <h3 className="aircraft-browser-results__title">
                                                    {result.aircraftName}
                                                </h3>
                                                <ul className="aircraft-browser-results__list">
                                                    {result.headRoom && <li>Head room: {result.headRoom}M</li>}
                                                    {result.speed && <li>Speed (MPH): {result.speed}</li>}
                                                    {result.passengerscount && (
                                                        <li>Passengers: {result.passengerscount}</li>
                                                    )}
                                                    {result.range && <li>Range: {result.range}</li>}
                                                </ul>
                                            </div>
                                        </article>
                                    </SwiperSlide>
                                );
                            }
                            return null;
                        })}
                    </Swiper>
                </div>
            )}
            {!results && (
                <div className="aircraft-browser-results__placeholder-container">
                    <div className="aircraft-browser-results__placeholder-outer">
                        <div className="aircraft-browser-results__placeholder-inner">
                            {showImageSlider && <Loading />}
                        </div>
                    </div>
                    {!showImageSlider && <Loading />}
                </div>
            )}
        </div>
    );
};

export default AircraftBrowserResults;
